import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, useParams } from "wouter";
import { BottomNav } from "@/components/BottomNav";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowLeft,
  Trophy,
  Skull,
  Swords,
  Loader2,
  LogIn,
  Calendar,
  ChevronRight,
} from "lucide-react";

interface RivalryData {
  mode: string;
  teamANames: string[];
  teamBNames: string[];
  lifetimeNet: number;
  wins: number;
  losses: number;
  ties: number;
  roundsPlayed: number;
  biggestWin: { amount: number; courseName: string; date: string } | null;
  biggestLoss: { amount: number; courseName: string; date: string } | null;
  matches: {
    roundId: string;
    date: string;
    courseName: string;
    matchFormat: string;
    netResult: number;
    teamHolesWon: {
      teamAHolesWon: number;
      teamBHolesWon: number;
      tiedHoles: number;
      teamACarriers?: { carriers: Record<string, { name: string; carries: number }>; shared: number };
      teamBCarriers?: { carriers: Record<string, { name: string; carries: number }>; shared: number };
    } | null;
  }[];
}

function formatMoney(amount: number): string {
  const abs = Math.abs(amount);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className ?? "h-24"}`} />;
}

export default function Rivalry() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const params = useParams();

  const teamKeyParam = params?.teamKey as string | undefined;
  const personA = params?.personA as string | undefined;
  const personB = params?.personB as string | undefined;
  const legacyMode = params?.mode as string | undefined;
  const teamAParam = params?.teamA as string | undefined;
  const teamBParam = params?.teamB as string | undefined;

  let apiUrl: string;
  let queryKeyParts: string[];
  let canFetch: boolean;

  if (teamKeyParam) {
    apiUrl = `/api/rivalry?teamKey=${teamKeyParam}`;
    queryKeyParts = ["/api/rivalry", "teamKey", teamKeyParam];
    canFetch = !!teamKeyParam;
  } else if (personA && personB) {
    apiUrl = `/api/rivalry?mode=1v1&teamA=${personA}&teamB=${personB}`;
    queryKeyParts = ["/api/rivalry", "1v1", personA, personB];
    canFetch = true;
  } else {
    apiUrl = `/api/rivalry?mode=${legacyMode}&teamA=${teamAParam}&teamB=${teamBParam}`;
    queryKeyParts = ["/api/rivalry", legacyMode ?? "", teamAParam ?? "", teamBParam ?? ""];
    canFetch = !!legacyMode && !!teamAParam && !!teamBParam;
  }

  const { data: rivalry, isLoading, isError, error } = useQuery<RivalryData>({
    queryKey: queryKeyParts,
    queryFn: async () => {
      const res = await fetch(apiUrl, { credentials: "include" });
      if (res.status === 403) throw new Error("unauthorized");
      if (!res.ok) throw new Error("Failed to load rivalry");
      return res.json();
    },
    enabled: canFetch && isAuthenticated,
    retry: false,
  });

  const [selectedMatch, setSelectedMatch] = useState<RivalryData["matches"][0] | null>(null);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <LogIn className="w-12 h-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-bold text-gray-800 mb-2" data-testid="text-unauthorized">Sign in required</h2>
        <BottomNav />
      </div>
    );
  }

  const teamALabel = rivalry ? rivalry.teamANames.join(" & ") : "";
  const teamBLabel = rivalry ? rivalry.teamBNames.join(" & ") : "";

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="sticky top-0 z-40 bg-[#064E3B] text-white px-4 pb-3 flex items-center gap-3 safe-area-top">
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : navigate("/ledger")}
          className="p-1 -ml-1 rounded-lg hover:bg-white/10"
          data-testid="button-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <Swords className="w-4 h-4 text-[#FACC15] flex-shrink-0" />
          <h1 className="text-base font-bold truncate" data-testid="text-page-title">
            {rivalry?.mode === "2v2" ? "Team Rivalry" : "Rivalry"}
          </h1>
        </div>
      </header>

      <div className="px-4 pt-4 space-y-4">
        {isLoading ? (
          <>
            <SkeletonBlock className="h-56" />
            <SkeletonBlock className="h-20" />
            <SkeletonBlock className="h-48" />
          </>
        ) : isError ? (
          <div className="text-center py-12" data-testid="text-error">
            <Skull className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-600">
              {(error as Error)?.message === "unauthorized"
                ? "You don't have access to this rivalry"
                : "Something went wrong"}
            </p>
            <button
              onClick={() => navigate("/ledger")}
              className="mt-4 text-sm text-[#064E3B] font-semibold underline"
              data-testid="button-error-back"
            >
              Back to Ledger
            </button>
          </div>
        ) : rivalry ? (
          <>
            <RivalryHeader
              rivalry={rivalry}
              teamALabel={teamALabel}
              teamBLabel={teamBLabel}
            />
            <KeyStats rivalry={rivalry} />
            <MatchHistory
              matches={rivalry.matches}
              onSelect={setSelectedMatch}
            />
          </>
        ) : (
          <div className="text-center py-12 text-gray-400" data-testid="text-no-data">
            No rivalry data
          </div>
        )}
      </div>

      <MatchDetailSheet match={selectedMatch} onClose={() => setSelectedMatch(null)} />
      <BottomNav />
    </div>
  );
}

function RivalryHeader({
  rivalry,
  teamALabel,
  teamBLabel,
}: {
  rivalry: RivalryData;
  teamALabel: string;
  teamBLabel: string;
}) {
  const aIsUp = rivalry.lifetimeNet > 0;
  const isEven = rivalry.lifetimeNet === 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="card-rivalry-header">
      <div className="bg-[#064E3B] px-5 py-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0 text-center">
            <p className={`text-lg font-black ${aIsUp ? "text-[#FACC15]" : "text-white"} truncate`} data-testid="text-team-a">
              {teamALabel}
            </p>
          </div>
          <div className="flex-shrink-0 px-2">
            <span className="text-emerald-300 text-xs font-bold">VS</span>
          </div>
          <div className="flex-1 min-w-0 text-center">
            <p className={`text-lg font-black ${!aIsUp && !isEven ? "text-[#FACC15]" : "text-white"} truncate`} data-testid="text-team-b">
              {teamBLabel}
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          {isEven ? (
            <p className="text-2xl font-black text-gray-300" data-testid="text-rivalry-net">
              EVEN
            </p>
          ) : (
            <>
              <p className="text-2xl font-black text-[#FACC15]" data-testid="text-rivalry-net">
                {aIsUp ? teamALabel : teamBLabel} is up {formatMoney(Math.abs(rivalry.lifetimeNet))}
              </p>
            </>
          )}
          <p className="text-emerald-200 text-xs mt-1" data-testid="text-rivalry-record">
            {rivalry.wins}-{rivalry.losses}{rivalry.ties > 0 ? `-${rivalry.ties}` : ""} · {rivalry.roundsPlayed} round{rivalry.roundsPlayed !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

function KeyStats({ rivalry }: { rivalry: RivalryData }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="section-key-stats">
      <div className="bg-green-50 rounded-2xl border border-gray-100 p-3.5">
        <div className="flex items-center gap-1.5 mb-2">
          <Trophy className="w-4 h-4 text-green-600" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Biggest Win</span>
        </div>
        {rivalry.biggestWin ? (
          <>
            <div className="text-lg font-bold text-green-600" data-testid="text-biggest-win">
              +{formatMoney(rivalry.biggestWin.amount)}
            </div>
            <div className="text-xs text-gray-400 mt-0.5 truncate">
              {rivalry.biggestWin.courseName} · {formatDate(rivalry.biggestWin.date)}
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-400 mt-2">None yet</div>
        )}
      </div>

      <div className="bg-red-50 rounded-2xl border border-gray-100 p-3.5">
        <div className="flex items-center gap-1.5 mb-2">
          <Skull className="w-4 h-4 text-red-500" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Biggest Loss</span>
        </div>
        {rivalry.biggestLoss ? (
          <>
            <div className="text-lg font-bold text-red-500" data-testid="text-biggest-loss">
              {formatMoney(rivalry.biggestLoss.amount)}
            </div>
            <div className="text-xs text-gray-400 mt-0.5 truncate">
              {rivalry.biggestLoss.courseName} · {formatDate(rivalry.biggestLoss.date)}
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-400 mt-2">None yet</div>
        )}
      </div>
    </div>
  );
}

function MatchHistory({
  matches,
  onSelect,
}: {
  matches: RivalryData["matches"];
  onSelect: (m: RivalryData["matches"][0]) => void;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-match-history">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-[#064E3B]" />
        <h3 className="text-sm font-bold text-gray-900">Match History</h3>
        <span className="text-xs text-gray-400 ml-auto">{matches.length} match{matches.length !== 1 ? "es" : ""}</span>
      </div>

      {matches.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6" data-testid="text-no-matches">
          No matches found between these players
        </p>
      ) : (
        <div className="space-y-1">
          {matches.map((m) => {
            const netColor = m.netResult > 0 ? "text-green-600" : m.netResult < 0 ? "text-red-500" : "text-gray-400";
            return (
              <button
                key={m.roundId}
                onClick={() => onSelect(m)}
                className="w-full flex items-center justify-between py-2.5 px-1 rounded-lg active:bg-gray-50 transition-colors text-left"
                data-testid={`match-row-${m.roundId}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{m.courseName}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-400">{formatDate(m.date)} · {m.matchFormat}</p>
                    {m.teamHolesWon && (
                      <span className="text-xs text-emerald-600 font-medium" data-testid={`holes-won-${m.roundId}`}>
                        Holes {m.teamHolesWon.teamAHolesWon}–{m.teamHolesWon.teamBHolesWon}
                      </span>
                    )}
                  </div>
                  {m.teamHolesWon?.teamACarriers && m.teamHolesWon?.teamBCarriers && (
                    <div className="flex gap-3 text-xs text-gray-400" data-testid={`carriers-${m.roundId}`}>
                      <span>{Object.values(m.teamHolesWon.teamACarriers.carriers).map(c => `${c.name}: ${c.carries}`).join(', ')}{m.teamHolesWon.teamACarriers.shared > 0 ? `, Shared: ${m.teamHolesWon.teamACarriers.shared}` : ''}</span>
                      <span>|</span>
                      <span>{Object.values(m.teamHolesWon.teamBCarriers.carriers).map(c => `${c.name}: ${c.carries}`).join(', ')}{m.teamHolesWon.teamBCarriers.shared > 0 ? `, Shared: ${m.teamHolesWon.teamBCarriers.shared}` : ''}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-sm font-bold ${netColor}`}>
                    {m.netResult > 0 ? "+" : ""}{formatMoney(m.netResult)}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MatchDetailSheet({
  match,
  onClose,
}: {
  match: RivalryData["matches"][0] | null;
  onClose: () => void;
}) {
  if (!match) return null;

  const netColor = match.netResult > 0 ? "text-green-600" : match.netResult < 0 ? "text-red-500" : "text-gray-400";

  return (
    <Sheet open={!!match} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[60dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="text-left pb-3">
          <SheetTitle className="text-lg font-bold text-gray-900">
            {match.courseName}
          </SheetTitle>
          <p className="text-xs text-gray-400">
            {formatDate(match.date)} · {match.matchFormat}
          </p>
        </SheetHeader>
        <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
          <span className="text-sm font-medium text-gray-600">Net Result</span>
          <span className={`text-2xl font-black ${netColor}`} data-testid="text-match-net">
            {match.netResult > 0 ? "+" : ""}{formatMoney(match.netResult)}
          </span>
        </div>
      </SheetContent>
    </Sheet>
  );
}
